import { debug } from "./debug";
import {
  DEFAULT_MAX_TOKENS,
  TOKEN_ESCALATION_FACTOR,
  MAX_TOKEN_CEILING,
  DEFAULT_MAX_RETRIES,
  RETRY_BACKOFF_BASE_MS,
} from "./config";
import { TruncationError, isNonRetryable } from "./errors";
import { buildPrompt } from "./prompts";
import { validatePlan } from "./validatePlan";
import { _dispatchToProvider } from "./providers";
import { buildRefinementPrompt, finalisePlan } from "./finalisePlan";
import { createRepairLog, countValidationIssues } from "./diagnostics";
import { withCallTimeLimit, CallTimeoutError } from "./callTimeLimit";

// The person chose the first plan while the refinement call was running.
class RefinementSkipped extends Error {
  constructor() {
    super("Refinement skipped");
    this.name = "RefinementSkipped";
  }
}


const generateWorkoutPlan = async (
  userProfile,
  llmSettings,
  maxRetries = DEFAULT_MAX_RETRIES,
  signal = null,
  onStatus = null,
  onThinking = null,
  onUsage = null,
  onDiagnostics = null,
  onRefining = null,
) => {
  const status = (msg) => {
    if (onStatus) onStatus(msg);
  };
  // One entry per provider call, mirroring onUsage: a truncation retry and the
  // refinement pass each report their own repair/validation counts, so the
  // aggregate can tell a pass that fires once from one that fires every time.
  const diagnose = (entry) => {
    if (onDiagnostics) onDiagnostics(entry);
  };

  status("Building prompt...");
  const prompt = buildPrompt(userProfile);

  console.log(
    `[WorkoutAI] Generating with ${llmSettings.provider} (${llmSettings.model})`,
  );
  debug.group("generateWorkoutPlan");
  debug.log("Provider:", llmSettings.provider);
  debug.log("Model:", llmSettings.model);
  debug.log("API Key present:", !!llmSettings.apiKey);
  debug.log("API Key prefix:", llmSettings.apiKey?.substring(0, 8) + "...");
  debug.log("Prompt length:", prompt.length);
  debug.log("Full prompt:", prompt);
  debug.groupEnd();

  // Every provider call gets a time limit (#167), so a stall becomes a failed
  // attempt and the retry starts. Ollama is exempt: it runs on local hardware
  // that can take minutes, and the server's own proxy limit covers it.
  const callProvider = (callPrompt, maxTokens, callSignal = signal) => {
    if (llmSettings.provider === "ollama") {
      return _dispatchToProvider(
        callPrompt,
        llmSettings,
        callSignal,
        maxTokens,
        onThinking,
        onUsage,
      );
    }
    return withCallTimeLimit(
      (limitSignal, touch) =>
        _dispatchToProvider(
          callPrompt,
          llmSettings,
          limitSignal,
          maxTokens,
          // Only wrapped when there is one: some providers stream only when
          // handed an onThinking.
          onThinking &&
            ((text) => {
              touch();
              onThinking(text);
            }),
          onUsage,
        ),
      { signal: callSignal },
    );
  };

  let lastError;
  let currentMaxTokens = DEFAULT_MAX_TOKENS;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      debug.log(
        `--- Attempt ${attempt + 1}/${maxRetries + 1} (maxTokens: ${currentMaxTokens}) ---`,
      );
      const attemptLabel = attempt > 0 ? ` (retry ${attempt})` : "";
      status(`Sending request to ${llmSettings.model}${attemptLabel}...`);

      const planObject = await callProvider(prompt, currentMaxTokens);

      status("Parsing response...");
      // finalisePlan mutates planObject in place (unit coercion, transition
      // injection, timing scaling), so the tree the model actually authored is
      // gone once it returns. Snapshot it first — the refinement pass below
      // needs the model's own plan, not ours. A workout tree is a few KB of
      // plain JSON, so the clone is free next to the provider call it saves.
      const authoredPlan = JSON.parse(JSON.stringify(planObject.workout_plan));
      const repairLog = createRepairLog();
      const finalised = finalisePlan(
        planObject,
        userProfile,
        repairLog.onRepair,
      );

      // --- Deterministic validation + single optional refinement (issue #16) ---
      // The post-processing above fixes what it can (units, transitions,
      // timing). validatePlan then flags what remains; only a BLOCKING residue
      // triggers ONE extra generation that re-sends the issues for correction.
      // Never fires on a clean plan, so the happy path keeps its single call.
      const refineEnabled = llmSettings.refine !== false;
      const validation = validatePlan(finalised.workout_plan, userProfile);
      diagnose({
        provider: llmSettings.provider,
        model: llmSettings.model,
        refinement: false,
        repairs: repairLog.counts,
        validation: countValidationIssues(validation.issues),
        blocking: validation.blocking,
      });

      if (validation.blocking && refineEnabled) {
        debug.warn("Validation flagged blocking issues:", validation.issues);
        status("Refining plan...");
        // The first plan is complete and playable, so the person can take it
        // instead of waiting for the second call (#166). Skipping races the
        // call rather than waiting for it to notice the abort, since not every
        // SDK checks its signal while it waits for the first byte.
        const refineController = new AbortController();
        const onStop = () => refineController.abort();
        if (signal) signal.addEventListener("abort", onStop, { once: true });
        let skipRefinement;
        const skipped = new Promise((_, reject) => {
          skipRefinement = () => {
            reject(new RefinementSkipped());
            refineController.abort();
          };
        });
        skipped.catch(() => {});
        if (onRefining) onRefining(skipRefinement);
        try {
          const refinePrompt = buildRefinementPrompt(
            prompt,
            authoredPlan,
            validation.issues,
          );
          const refinedRaw = await Promise.race([
            callProvider(
              refinePrompt,
              currentMaxTokens,
              refineController.signal,
            ),
            skipped,
          ]);
          const refineLog = createRepairLog();
          const refinedFinal = finalisePlan(
            refinedRaw,
            userProfile,
            refineLog.onRepair,
          );
          const refinedValidation = validatePlan(
            refinedFinal.workout_plan,
            userProfile,
          );
          diagnose({
            provider: llmSettings.provider,
            model: llmSettings.model,
            refinement: true,
            repairs: refineLog.counts,
            validation: countValidationIssues(refinedValidation.issues),
            blocking: refinedValidation.blocking,
          });
          // Accept the refinement only if it actually cleared the blockers.
          if (!refinedValidation.blocking) {
            refinedFinal.workout_plan.meta = {
              ...(refinedFinal.workout_plan.meta || {}),
              validation: { refined: true, issues: refinedValidation.issues },
            };
            console.log("[WorkoutAI] Refinement resolved blocking issues");
            debug.log("Plan label:", refinedFinal.workout_plan.label);
            return refinedFinal;
          }
          debug.warn("Refinement did not clear blockers; keeping original");
        } catch (refineError) {
          // A failed refinement must never discard the original good-enough plan.
          if (refineError instanceof RefinementSkipped) {
            console.log("[WorkoutAI] Refinement skipped; using the first plan");
          } else if (signal?.aborted || refineError?.name === "AbortError") {
            throw refineError;
          } else {
            console.warn(
              "[WorkoutAI] Refinement pass failed:",
              refineError.message,
            );
            debug.error("Refinement error:", refineError);
          }
        } finally {
          if (signal) signal.removeEventListener("abort", onStop);
          if (onRefining) onRefining(null);
        }
      }

      finalised.workout_plan.meta = {
        ...(finalised.workout_plan.meta || {}),
        validation: { refined: false, issues: validation.issues },
      };

      console.log("[WorkoutAI] Successfully generated workout plan");
      debug.log("Plan label:", finalised.workout_plan.label);
      debug.log("Top-level children:", finalised.workout_plan.children?.length);
      return finalised;
    } catch (error) {
      lastError = error;
      console.warn(`[WorkoutAI] Attempt ${attempt + 1} failed:`, error.message);
      debug.error("Full error:", error);

      // Non-retryable errors — bail immediately
      if (isNonRetryable(error)) {
        throw error;
      }

      // Truncation — escalate token limit for next attempt
      if (error instanceof TruncationError) {
        const nextTokens = Math.min(
          currentMaxTokens * TOKEN_ESCALATION_FACTOR,
          MAX_TOKEN_CEILING,
        );
        if (nextTokens > currentMaxTokens) {
          console.warn(
            `[WorkoutAI] Truncation detected. Escalating tokens: ${currentMaxTokens} → ${nextTokens}`,
          );
          currentMaxTokens = nextTokens;
          status(
            `Response truncated — retrying with higher token limit (${nextTokens})...`,
          );
        } else {
          console.warn(
            `[WorkoutAI] Truncation detected but already at token ceiling (${MAX_TOKEN_CEILING})`,
          );
        }
      }

      if (error instanceof CallTimeoutError && attempt < maxRetries) {
        status(`${error.message} Trying again...`);
      }

      if (attempt < maxRetries) {
        const delay = RETRY_BACKOFF_BASE_MS * Math.pow(2, attempt);
        debug.log(`Retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  // All retries failed
  throw new Error(
    `Failed to generate workout after ${maxRetries + 1} attempts. ${
      lastError?.message || "Unknown error"
    }`,
  );
};

export { generateWorkoutPlan };
